import { useState, type CSSProperties } from 'react';
import { ProfileGlyph } from './icons';
import { useAccount } from '../auth/AccountContext';
import { AccountMenu } from './AccountMenu';
import { NavigationDock } from './NavigationDock';
import type { ArtRenderer, CoinView } from './model';
import { PointsAmount } from '../PointsAmount';
import { ExploreFilters } from './ExploreFilters';
import type { ExploreFilter } from './exploreModel';
export const NAV_VIEWS = ['Arena', 'Explore', 'Leaderboard'] as const;

export function TopBar({
  view,
  previewBalance,
  accountPoints = false,
  onView,
  onProfile,
  feed = 'live',
  onFeedChange,
  availablePredictions,
  coins,
  art,
  onPredictions,
  exploreFilter = 'Watching',
  onExploreFilter,
}: {
  view: string;
  previewBalance?: string;
  accountPoints?: boolean;
  onView: (view: string) => void;
  onProfile: () => void;
  feed?: 'live' | 'game';
  onFeedChange?: (feed: 'live' | 'game') => void;
  availablePredictions?: { live: boolean; game: boolean };
  coins?: CoinView[];
  art?: ArtRenderer;
  onPredictions?: () => void;
  exploreFilter?: ExploreFilter;
  onExploreFilter?: (filter: ExploreFilter) => void;
}) {
  const account = useAccount();
  const activeIndex = NAV_VIEWS.findIndex((item) => item === view);
  const showBalance = previewBalance !== undefined;
  const [indicatorIndex, setIndicatorIndex] = useState(
    Math.max(0, activeIndex),
  );
  // Keep the last position while fading out on Profile or another non-tab page.
  if (activeIndex >= 0 && activeIndex !== indicatorIndex)
    setIndicatorIndex(activeIndex);
  return (
    <header className={'top-bar' + (view === 'Explore' ? ' is-pinned' : '')}>
      <div className="header-brand">
        <button
          type="button"
          className="rd-logo"
          onClick={() => onView('Arena')}
          aria-label="Run or Die home"
        >
          <img src="/images/header-logo.png" width="52" height="52" alt="" />
        </button>
        <button
          type="button"
          className="header-feed-switch"
          role="switch"
          aria-label="Game launches"
          aria-checked={feed === 'game'}
          disabled={!onFeedChange}
          title={`${feed === 'game' ? 'Game' : 'Live'} launches${availablePredictions?.[feed] ? ' — prediction available' : ''}`}
          onClick={() => onFeedChange?.(feed === 'live' ? 'game' : 'live')}
        >
          <span className={feed === 'live' ? 'is-active' : ''}>live</span>
          <i aria-hidden="true">
            <b />
          </i>
          <span className={feed === 'game' ? 'is-active' : ''}>game</span>
        </button>
      </div>
      <NavigationDock
        view={view}
        coins={coins}
        art={art}
        onPredictions={onPredictions ?? (() => onView('My predictions'))}
        filters={
          view === 'Explore' && onExploreFilter ? (
            <ExploreFilters filter={exploreFilter} onFilter={onExploreFilter} />
          ) : undefined
        }
      >
        <nav
          className="nav-pill"
          aria-label="Main navigation"
          data-selected={activeIndex >= 0}
          style={
            {
              '--nav-index': indicatorIndex,
              '--nav-count': NAV_VIEWS.length,
            } as CSSProperties
          }
        >
          <span className="nav-selection" aria-hidden="true" />
          {NAV_VIEWS.map((item) => (
            <button
              key={item}
              type="button"
              className={view === item ? 'is-active' : ''}
              aria-current={view === item ? 'page' : undefined}
              aria-label={item}
              onClick={() => onView(item)}
            >
              <img
                className="mobile-nav-icon"
                src={'/images/mobile-tab-' + item.toLowerCase() + '.svg'}
                alt=""
                aria-hidden="true"
              />
              <span className="nav-label">{item}</span>
            </button>
          ))}
        </nav>
      </NavigationDock>
      <div className="top-identity">
        <button
          type="button"
          className="identity-pill points-tab"
          onClick={() => onView('Points')}
          aria-current={view === 'Points' ? 'page' : undefined}
          aria-label={
            showBalance
              ? `Points ${accountPoints ? 'balance' : 'preview balance'}: ${previewBalance} PTS`
              : 'Points'
          }
          title={accountPoints ? 'Your points' : 'Demo points preview'}
        >
          {showBalance ? (
            <PointsAmount value={previewBalance} />
          ) : (
            <span>Points</span>
          )}
        </button>
        {account ? (
          <AccountMenu account={account} />
        ) : (
          <button
            type="button"
            className="avatar-button"
            onClick={onProfile}
            aria-label="Your profile"
          >
            <ProfileGlyph />
          </button>
        )}
      </div>
    </header>
  );
}
